import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
  buildBulkBreedingCardsHtml,
  printHtml,
  DEFAULT_SETTINGS,
} from "@/lib/equi";
import BreedingCardDialog from "@/components/BreedingCardDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, FileText, Printer, Loader2, Search } from "lucide-react";

const BC_FIELDS = [
  { key: "name", label: "Mare Name", type: "text" },
  { key: "stallion", label: "Stallion", type: "text" },
  { key: "owner", label: "Owner", type: "text" },
  { key: "confirmed_in_foal_date", label: "Confirmed in Foal", type: "date" },
  { key: "estimated_due_date", label: "Estimated Due Date", type: "date" },
  { key: "foal_out_date", label: "Foal Out Date", type: "date" },
];

export default function Mares() {
  const { readOnly } = useAuth();
  const [mares, setMares] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cardMare, setCardMare] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([api.get("/mares"), api.get("/settings")]);
      setMares(m.data);
      setSettings(s.data);
    } catch {
      toast.error("Could not load mares");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addMare = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/mares", { name, stallion: "" });
      setNewName("");
      load();
    } catch {
      toast.error("Failed to add mare");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/mares/${editing.id}`, {
        name: editing.name,
        stallion: editing.stallion || "",
        owner: editing.owner || "",
        confirmed_in_foal_date: editing.confirmed_in_foal_date || "",
        estimated_due_date: editing.estimated_due_date || "",
        foal_out_date: editing.foal_out_date || "",
        deceased: !!editing.deceased,
      });
      toast.success("Mare updated");
      setEditing(null);
      load();
    } catch {
      toast.error("Failed to update mare");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/mares/${deleteTarget.id}`);
      toast.success("Mare deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete mare");
    }
  };

  const printAll = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.get(`/breeding-cards/year/${year}`);
      if (!data.cards || data.cards.length === 0) {
        toast.info(`No mares have records for ${year} yet`);
        return;
      }
      printHtml(buildBulkBreedingCardsHtml(data.cards, settings, year));
    } catch {
      toast.error("Failed to build cards");
    } finally {
      setBulkBusy(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = (q
    ? mares.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          (m.stallion || "").toLowerCase().includes(q) ||
          (m.owner || "").toLowerCase().includes(q)
      )
    : mares
  )
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Master Mares</h1>
          <p className="text-muted-foreground text-sm">Your roster and each mare&apos;s breeding-card details.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-24"
            data-testid="mares-year-input"
          />
          <Button onClick={printAll} disabled={bulkBusy} className="gap-2" data-testid="mares-print-all-btn">
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Print All Cards
          </Button>
        </div>
      </div>

      {/* Search + add mare */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search mares by name, stallion, owner…"
            className="pl-9"
            data-testid="mares-search-input"
          />
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 sm:ml-auto">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addMare()}
              placeholder="New mare name…"
              className="max-w-xs"
              data-testid="mares-new-name-input"
            />
            <Button onClick={addMare} disabled={!newName.trim()} className="gap-2" data-testid="mares-add-btn">
              <Plus className="h-4 w-4" /> Add Mare
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : mares.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl">
          <p className="text-lg font-semibold">No mares yet</p>
          <p className="text-muted-foreground text-sm mt-1">Add your first mare above to build your roster.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl" data-testid="mares-no-matches">
          <p className="text-lg font-semibold">No mares match “{search.trim()}”</p>
          <p className="text-muted-foreground text-sm mt-1">Try a different name, stallion, or owner.</p>
        </div>
      ) : (
        <div className="border border-[#C4C8D6] rounded-md bg-card overflow-x-auto equi-scroll">
          <table className="w-full text-[13px] min-w-[640px]">
            <thead>
              <tr className="bg-primary text-primary-foreground text-[11px] uppercase tracking-wide [&>th]:border-r [&>th]:border-white/15 last:[&>th]:border-r-0">
                <th className="px-4 py-3 text-left font-semibold">Mare</th>
                <th className="px-4 py-3 text-left font-semibold">Stallion</th>
                <th className="px-4 py-3 text-left font-semibold">Owner</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-b border-[#C4C8D6] hover:bg-primary/5 [&>td]:border-r [&>td]:border-[#C4C8D6] last:[&>td]:border-r-0" data-testid={`mare-row-${m.id}`}>
                  <td className="px-4 py-3 font-semibold">
                    {m.name}
                    {m.deceased && (
                      <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-muted text-muted-foreground border border-border" data-testid={`mare-deceased-badge-${m.id}`}>
                        Deceased
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{m.stallion || <span className="text-border">—</span>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{m.owner || <span className="text-border">—</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setCardMare(m)} data-testid={`mare-card-${m.id}`}>
                        <FileText className="h-4 w-4" /> Card
                      </Button>
                      {!readOnly && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing({ ...m })} data-testid={`mare-edit-${m.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleteTarget(m)} data-testid={`mare-delete-${m.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {editing?.name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <>
              <div className="grid grid-cols-2 gap-4 py-2">
                {BC_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={`m-${f.key}`}>{f.label}</Label>
                    <Input
                      id={`m-${f.key}`}
                      type={f.type}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, [f.key]: e.target.value }))}
                      data-testid={`mare-field-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <div>
                  <p className="text-sm font-semibold">Status</p>
                  <p className="text-xs text-muted-foreground">{editing.deceased ? "Marked as deceased" : "Active"}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={editing.deceased ? "text-emerald-600" : "text-destructive"}
                  onClick={() => setEditing((s) => ({ ...s, deceased: !s.deceased }))}
                  data-testid="mare-deceased-toggle"
                >
                  {editing.deceased ? "Mark Active" : "Mark Deceased"}
                </Button>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="mare-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the mare and all of her daily & teasing records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="mare-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BreedingCardDialog
        open={!!cardMare}
        onOpenChange={(o) => !o && setCardMare(null)}
        mare={cardMare}
        year={year}
        settings={settings}
        onSaved={() => load()}
      />
    </div>
  );
}
